/**
 * แบ่ง job ทั้งวันของ 1 shift ออกเป็น Firestore document หลายใบ (schema v2)
 *
 * ทำไมต้องมี: ของเดิมยัด job ทั้งวันลง document เดียวโดยเก็บ `jobs` เป็น map
 * ของ job เป็นพันตัว Firestore สร้าง index entry ให้ทุก field ที่ซ้อนอยู่ข้างใน
 * วันธรรมดามี ~27,500 entries ซึ่งอยู่ที่ขอบเพดาน 40,000 ต่อ document พอคืนไหน
 * คนเยอะกว่าปกติจึงทะลุแล้ว write ถูก reject (ร้านลูกค้าหายทุกวันเสาร์ 5/5 สัปดาห์)
 *
 * v2 จึงเก็บ payload จริงเป็น JSON string ใน `_jobs` — string ทั้งก้อนนับเป็น
 * 1 index entry ไม่ว่าจะมี job กี่ตัว เพดาน 40,000 จบตรงนี้
 *
 * ส่วนการแตกเป็นหลาย doc กันเพดานคนละตัวคือ **ขนาด document 1 MiB**
 * ตัวคุมจริงคือ JOBS_CHUNK_MAX_BYTES ส่วน JOBS_CHUNK_SIZE เป็นแค่ตัวกันสำรอง
 * (อย่าสลับเหตุผลสองอันนี้ ไม่งั้นจะจูนผิดตัว)
 *
 * ไฟล์นี้ตั้งใจให้เป็น pure function ล้วน ไม่แตะ Firestore เพื่อให้เทสต์ได้ตรง ๆ
 * ตัวที่เอาไปเขียนจริงคือ addDayJobs() ใน useFirebase.ts
 */

export const JOBS_SCHEMA_VERSION = "v2";
export const JOBS_CHUNK_SIZE = 300;              // ตัวกันสำรอง
export const JOBS_CHUNK_MAX_BYTES = 400 * 1024;  // ตัวคุมจริง กันเพดาน 1 MiB ต่อ doc

export interface JobChunkDoc {
  id: string;
  payload: Record<string, any>;
}

/**
 * ขนาดจริงเป็นไบต์ ไม่ใช่จำนวนตัวอักษร — ภาษาไทยกิน 3 byte ต่อตัวใน UTF-8
 * ถ้านับด้วย .length จะประเมินต่ำไป 3 เท่าแล้ว doc ทะลุ 1 MiB โดยไม่รู้ตัว
 */
export function utf8Length(text: string): number {
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(text).length;
  }

  return text.length * 3; // fallback แบบเผื่อไว้มากสุด
}

/**
 * แบ่ง map ของ job เป็นก้อนย่อย โดยตัดเมื่อชนเพดานขนาดหรือจำนวน อย่างใดอย่างหนึ่งก่อน
 * key ของ job ไม่ซ้ำกันข้ามก้อน ฝั่งอ่านจึง merge ก้อนไหนก่อนก็ได้ผลเท่ากัน
 */
export function chunkJobs(jobs: Record<string, any>): Record<string, any>[] {
  const entries = Object.entries(jobs || {});

  // ไม่มี job ก็ยังต้องเขียน 1 ใบ เพื่อให้รู้ว่าปิดร้านวันนั้นแล้วจริง
  if (entries.length === 0) return [{}];

  const chunks: Record<string, any>[] = [];
  let current: Record<string, any> = {};
  let count = 0;
  let bytes = 0;

  for (const [key, value] of entries) {
    const size = utf8Length(JSON.stringify(value)) + utf8Length(key) + 4;

    if (count > 0 && (count >= JOBS_CHUNK_SIZE || bytes + size > JOBS_CHUNK_MAX_BYTES)) {
      chunks.push(current);
      current = {};
      count = 0;
      bytes = 0;
    }

    current[key] = value;
    count++;
    bytes += size;
  }

  chunks.push(current);

  return chunks;
}

/**
 * id ลงท้าย `:NNN` เสมอแม้มีก้อนเดียว จะได้แยกออกจาก doc v1 ที่ id ไม่มี suffix
 * (ตอนปิดร้านซ้ำ ใบ v1 เดิมต้องถูกลบทิ้ง ไม่งั้นจะถูกนับซ้ำ)
 */
export function jobChunkDocId(baseDocId: string, index: number): string {
  return `${baseDocId}:${String(index).padStart(3, "0")}`;
}

/**
 * แปลง payload ปิดร้าน 1 ก้อน -> รายการ document ที่พร้อมเขียน
 *
 * `jobs` คงไว้เป็น map เปล่าเสมอ เผื่อมี client เก่าค้างอยู่ —
 * `_.values({})` ได้ `[]` ซึ่งปลอดภัย ต่างจาก `_.values("...")` ที่ได้ array ของตัวอักษร
 */
export function buildJobChunkDocs(baseDocId: string, data: Record<string, any>): JobChunkDoc[] {
  const { jobs, ...meta } = data || {};
  const chunks = chunkJobs(jobs);

  return chunks.map((chunk, index) => ({
    id: jobChunkDocId(baseDocId, index),
    payload: {
      ...meta,
      schema: JOBS_SCHEMA_VERSION,
      chunkIndex: index,
      chunkTotal: chunks.length,
      jobCount: Object.keys(chunk).length,
      jobs: {},
      _jobs: JSON.stringify(chunk),
    },
  }));
}
